/**
 * Géométrie du calque vectoriel du plan : tracés des pistes et des
 * remontées projetés à l'écran, coupés là où le relief les cache (tampon de
 * profondeur du panorama), pastilles numérotées, noms des remontées et
 * pictogrammes des bâtiments placés sans se chevaucher.
 */
import { buildingName, type Building, type BuildingKind, type Difficulty, type Heightfield, type Lift, type LiftKind, type LiftStatus, type Piste } from '@snowchain/shared';
import type { ClientStore } from '../../state/store';
import { visibleOnPanorama, type Painted } from './paint';
import { toScreen, type View } from './view';

export type Pt = [number, number];

export interface PisteShape {
  id: number;
  num: number;
  name: string;
  difficulty: Difficulty;
  open: boolean;
  works: boolean;
  closedReason: string | null;
  /** Portions visibles (attribut d de path SVG). */
  d: string;
  /** Pastille numérotée, null si la piste est entièrement cachée. */
  badge: Pt | null;
  length: number;
  drop: number;
}

export interface LiftShape {
  id: number;
  name: string;
  kind: LiftKind;
  status: LiftStatus;
  d: string;
  /** Marques du type de remontée (sièges, cabines). */
  marks: string;
  bottom: Pt;
  top: Pt;
  bottomSeen: boolean;
  topSeen: boolean;
  label: { x: number; y: number; angle: number } | null;
  length: number;
  rise: number;
}

export type PictoKind = 'restaurant' | 'snack' | 'secours' | 'ecole' | 'location' | 'caisses' | 'parking' | 'hebergement' | 'garage';

export interface Picto {
  key: string;
  kind: PictoKind;
  /** Position du pictogramme et point d'ancrage au sol. */
  x: number;
  y: number;
  ax: number;
  ay: number;
  ids: number[];
  label: string;
}

/** Pictogramme par type de bâtiment (les autres ne sont pas signalés). */
export const PICTO_OF: Partial<Record<BuildingKind, PictoKind>> = {
  restaurant: 'restaurant',
  snack_bar: 'snack',
  first_aid: 'secours',
  ski_school: 'ecole',
  shop_rental: 'location',
  ticket_office: 'caisses',
  chalet_accueil: 'caisses',
  parking: 'parking',
  hotel: 'hebergement',
  residence: 'hebergement',
  groomer_garage: 'garage',
};

/** Numéro stable de chaque piste : ordre des identifiants. */
export function pisteNumbers(pistes: Iterable<Piste>): Map<number, number> {
  const ids = [...pistes].map((p) => p.id).sort((a, b) => a - b);
  return new Map(ids.map((id, k) => [id, k + 1]));
}

/** Chemin SVG des suites de points visibles (les trous isolés sont comblés). */
function visibleRuns(pts: Pt[], seen: boolean[]): { d: string; runs: Pt[][] } {
  const n = pts.length;
  const ok = seen.slice();
  for (let i = 1; i < n - 1; i++) if (!ok[i] && ok[i - 1] && ok[i + 1]) ok[i] = true;
  const runs: Pt[][] = [];
  let cur: Pt[] = [];
  for (let i = 0; i < n; i++) {
    if (ok[i]) cur.push(pts[i] as Pt);
    else if (cur.length) {
      runs.push(cur);
      cur = [];
    }
  }
  if (cur.length) runs.push(cur);
  const d = runs
    .filter((r) => r.length > 1)
    .map((r) => 'M' + r.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join('L'))
    .join('');
  return { d, runs: runs.filter((r) => r.length > 1) };
}

function pistesShapes(store: ClientStore, v: View, painted: Painted, nums: Map<number, number>): PisteShape[] {
  const r = store.replica;
  if (!r) return [];
  const out: PisteShape[] = [];
  const badges: Pt[] = [];
  const sorted = [...r.pistes.values()].sort((a, b) => a.id - b.id);
  for (const p of sorted) {
    const tr = store.pisteTrack(p.id);
    if (!tr) continue;
    const { xs, ys, zs } = tr.path;
    const pts: Pt[] = [];
    const seen: boolean[] = [];
    for (let i = 0; i < xs.length; i++) {
      const x = xs[i] as number;
      const z = zs[i] as number;
      const h = ys[i] as number;
      pts.push(toScreen(v, x, z, h));
      seen.push(visibleOnPanorama(painted, v, x, z, h + 2));
    }
    const { d } = visibleRuns(pts, seen);
    // Pastille : point visible proche du tiers du tracé, à l'écart des autres.
    let badge: Pt | null = null;
    const n = pts.length;
    for (const f of [0.3, 0.45, 0.18, 0.6, 0.75, 0.1, 0.88]) {
      const target = Math.round((n - 1) * f);
      let best = -1;
      for (let k = 0; k < n / 6; k++) {
        for (const i of [target - k, target + k]) {
          if (i >= 0 && i < n && seen[i]) {
            best = i;
            break;
          }
        }
        if (best >= 0) break;
      }
      if (best < 0) continue;
      const c = pts[best] as Pt;
      if (badges.every((b) => Math.hypot(b[0] - c[0], b[1] - c[1]) > 20)) {
        badge = c;
        break;
      }
      badge ??= c;
    }
    if (badge) badges.push(badge);
    out.push({
      id: p.id,
      num: nums.get(p.id) ?? 0,
      name: p.name,
      difficulty: p.difficulty,
      open: p.open,
      works: (p.progress ?? 1) < 1,
      closedReason: p.closedReason,
      d,
      badge,
      length: p.length,
      drop: p.drop,
    });
  }
  return out;
}

/** Point de câble vers hauteur d'affichage : le sol est exagéré, la hauteur du câble non. */
function cableHeight(hf: Heightfield, x: number, z: number, y: number, e: number): number {
  const g = hf.height(x, z);
  return g + (y - g) / e;
}

function liftShapes(store: ClientStore, v: View, painted: Painted): LiftShape[] {
  const r = store.replica;
  if (!r) return [];
  const hf = store.hf;
  const out: LiftShape[] = [];
  const buf = [0, 0, 0, 0, 0];
  for (const l of r.lifts.values()) {
    const geo = store.liftGeometry(l.id);
    if (!geo) continue;
    const path = geo.up;
    const pts: Pt[] = [];
    const seen: boolean[] = [];
    const n = Math.max(2, Math.ceil(path.length / 8));
    for (let k = 0; k <= n; k++) {
      path.sample((path.length * k) / n, buf);
      const x = buf[0] as number;
      const z = buf[2] as number;
      const h = cableHeight(hf, x, z, buf[1] as number, v.e);
      pts.push(toScreen(v, x, z, h));
      seen.push(visibleOnPanorama(painted, v, x, z, h, 30));
    }
    const { d, runs } = visibleRuns(pts, seen);
    const gb = hf.height(l.bottom[0], l.bottom[1]);
    const gt = hf.height(l.top[0], l.top[1]);
    const bottom = toScreen(v, l.bottom[0], l.bottom[1], gb);
    const top = toScreen(v, l.top[0], l.top[1], gt);
    const shape: LiftShape = {
      id: l.id,
      name: l.name,
      kind: l.kind,
      status: l.status,
      d,
      marks: liftMarks(l.kind, runs),
      bottom,
      top,
      bottomSeen: visibleOnPanorama(painted, v, l.bottom[0], l.bottom[1], gb + 4, 30),
      topSeen: visibleOnPanorama(painted, v, l.top[0], l.top[1], gt + 4, 30),
      label: liftLabel(runs),
      length: geo.length,
      rise: geo.rise,
    };
    out.push(shape);
  }
  return out;
}

/** Marques le long du câble : barrettes (télésiège), petites cabines (télécabine). */
function liftMarks(kind: LiftKind, runs: Pt[][]): string {
  if (kind === 'teleski') return '';
  const gap = kind === 'telecabine' ? 22 : 15;
  let d = '';
  for (const run of runs) {
    let acc = gap / 2;
    for (let i = 0; i < run.length - 1; i++) {
      const a = run[i] as Pt;
      const b = run[i + 1] as Pt;
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (len < 1e-6) continue;
      const ux = (b[0] - a[0]) / len;
      const uy = (b[1] - a[1]) / len;
      while (acc < len) {
        const x = a[0] + ux * acc;
        const y = a[1] + uy * acc;
        if (kind === 'telesiege') d += `M${(x - uy * 3.2).toFixed(1)} ${(y + ux * 3.2).toFixed(1)}L${(x + uy * 3.2).toFixed(1)} ${(y - ux * 3.2).toFixed(1)}`;
        else d += `M${(x - 2.2).toFixed(1)} ${(y + 1).toFixed(1)}h4.4v3.6h-4.4z`;
        acc += gap;
      }
      acc -= len;
    }
  }
  return d;
}

/** Nom d'une remontée : au milieu de sa plus longue portion visible, lisible (jamais à l'envers). */
function liftLabel(runs: Pt[][]): { x: number; y: number; angle: number } | null {
  let best: Pt[] | null = null;
  let bestLen = 0;
  for (const run of runs) {
    const a = run[0] as Pt;
    const b = run[run.length - 1] as Pt;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (len > bestLen) {
      bestLen = len;
      best = run;
    }
  }
  if (!best || bestLen < 40) return null;
  const a = best[0] as Pt;
  const b = best[best.length - 1] as Pt;
  const m = best[Math.floor(best.length / 2)] as Pt;
  let angle = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
  if (angle > 90) angle -= 180;
  if (angle < -90) angle += 180;
  // Décalé sur le côté pour ne pas recouvrir le câble.
  const rad = (angle * Math.PI) / 180;
  return { x: m[0] + Math.sin(rad) * 11, y: m[1] - Math.cos(rad) * 11 + 3.5, angle };
}

export const PICTO_LABEL: Record<PictoKind, string> = {
  restaurant: 'Restaurant',
  snack: 'Snack',
  secours: 'Poste de secours',
  ecole: 'École de ski',
  location: 'Location de matériel',
  caisses: 'Caisses',
  parking: 'Parking',
  hebergement: 'Hébergement',
  garage: 'Garage des dameuses',
};
function pictos(store: ClientStore, v: View): Picto[] {
  const r = store.replica;
  if (!r) return [];
  const hf = store.hf;
  const all = [...r.buildings.values()];
  const raw: { kind: PictoKind; b: Building; p: Pt }[] = [];
  for (const b of all) {
    const kind = PICTO_OF[b.kind];
    if (!kind) continue;
    raw.push({ kind, b, p: toScreen(v, b.x, b.z, hf.height(b.x, b.z)) });
  }
  // Regroupe les bâtiments de même type très proches à l'écran.
  const groups: { kind: PictoKind; ids: number[]; names: string[]; p: Pt }[] = [];
  for (const it of raw) {
    const g = groups.find((q) => q.kind === it.kind && Math.hypot(q.p[0] - it.p[0], q.p[1] - it.p[1]) < 18);
    const name = it.b.kind === 'chalet_accueil' ? `${buildingName(it.b, all)} (caisses)` : buildingName(it.b, all);
    if (g) {
      g.ids.push(it.b.id);
      g.names.push(name);
    } else groups.push({ kind: it.kind, ids: [it.b.id], names: [name], p: it.p });
  }
  // Placement glouton : au-dessus de l'ancrage, décalé tant qu'il chevauche.
  groups.sort((a, b) => a.p[1] - b.p[1] || a.p[0] - b.p[0]);
  const placed: Pt[] = [];
  const out: Picto[] = [];
  const size = 17;
  for (const g of groups) {
    let pos: Pt = [g.p[0], g.p[1] - 16];
    const tries: Pt[] = [];
    for (let ring = 0; ring < 5; ring++) {
      for (const dx of ring === 0 ? [0] : [ring * size, -ring * size]) tries.push([g.p[0] + dx, g.p[1] - 16 - (ring > 2 ? (ring - 2) * size : 0)]);
    }
    for (let ring = 1; ring < 6; ring++) tries.push([g.p[0], g.p[1] - 16 - ring * size]);
    for (const t of tries) {
      if (placed.every((q) => Math.abs(q[0] - t[0]) >= size || Math.abs(q[1] - t[1]) >= size)) {
        pos = t;
        break;
      }
    }
    placed.push(pos);
    const label = g.ids.length > 1 ? `${PICTO_LABEL[g.kind]} (${g.ids.length})` : (g.names[0] ?? PICTO_LABEL[g.kind]);
    out.push({ key: `${g.kind}:${g.ids.join(',')}`, kind: g.kind, x: pos[0], y: pos[1], ax: g.p[0], ay: g.p[1], ids: g.ids, label });
  }
  return out;
}

export interface Overlay {
  /** Hauteur de la vue (pixels CSS). */
  h: number;
  pistes: PisteShape[];
  lifts: LiftShape[];
  pictos: Picto[];
}

export function buildOverlay(store: ClientStore, v: View, painted: Painted): Overlay {
  const r = store.replica;
  const nums = pisteNumbers(r ? r.pistes.values() : []);
  return { h: v.h, pistes: pistesShapes(store, v, painted, nums), lifts: liftShapes(store, v, painted), pictos: pictos(store, v) };
}

/** Libellé d'état d'une remontée. */
export const LIFT_STATUS_LABEL: Record<LiftStatus, string> = {
  construction: 'En travaux',
  open: 'Ouverte',
  closed: 'Fermée',
  broken: 'En panne',
  wind: 'Arrêtée (vent)',
  'off-hours': 'Hors horaires',
};

/** Remontée en service (les autres sont dessinées estompées). */
export function liftRunning(l: Pick<Lift, 'status'>): boolean {
  return l.status === 'open';
}
